import React, { useState } from "react";
import ReactDOM from "react-dom";
import { SpotRequest, MergeSpotRequest, SpotAddRequest, getMergeRequestDistance, hasMergeRequestValidCoordinates, calculateDistance } from "@/common/model/SpotRequest";
import { isProtectedSpot } from "@/constants/spots";

export type ActionType = "approve" | "decline" | "review";

interface ConfirmActionDialogProps {
  request: SpotRequest;
  actionType: ActionType;
  isOpen: boolean;
  isLoading: boolean;
  onApprove: () => void;
  onDecline: (reason?: string) => void;
  onCancel: () => void;
  onUserClick?: (userId: number, username: string) => void;
}

const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  request,
  actionType,
  isOpen,
  isLoading,
  onApprove,
  onDecline,
  onCancel,
  onUserClick,
}) => {
  const [declineReason, setDeclineReason] = useState("");
  const [showDeclineReason, setShowDeclineReason] = useState(false);

  if (!isOpen) return null;

  // Helper to render clickable user link
  const renderUserLink = (userid: number | undefined, username: string | undefined) => {
    if (!userid) return null;

    return (
      <button
        onClick={() => onUserClick?.(userid, username || "")}
        className="text-sm text-gray-500 mt-1 hover:text-blue-600 transition-colors flex items-center gap-1 group"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <span className="group-hover:underline">
          {username ? `Requested by ${username}` : "View requesting user"}
        </span>
        <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </button>
    );
  };

  const handleDecline = () => {
    if (actionType === "review" && !showDeclineReason) {
      setShowDeclineReason(true);
      return;
    }
    onDecline(declineReason || undefined);
  };

  const renderMergeReviewContent = () => {
    const data = request.data as MergeSpotRequest;
    const distance = getMergeRequestDistance(data);
    const hasValidCoords = hasMergeRequestValidCoordinates(data);

    return (
      <>
        {/* Header */}
        <div className="text-center mb-5">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900">Merge Request Review</h3>
          <div className="flex justify-center">
            {renderUserLink(data.userid, data.username)}
          </div>
        </div>

        {/* Two-column spot comparison */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* Source Spot */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-xs font-semibold text-red-600 uppercase">Source (Delete)</span>
            </div>
            <h4 className="font-bold text-gray-900 mb-3">{data.sourcespotname}</h4>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Sessions</span>
                <span className="font-semibold text-gray-900">{data.sourcensessions ?? 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Reviews</span>
                <span className="font-semibold text-gray-900">{data.sourcenreviews ?? 0}</span>
              </div>
              {hasValidCoords && (
                <div className="pt-2 border-t border-red-200">
                  <div className="text-xs text-gray-400">
                    {data.sourceLat?.toFixed(5)}, {data.sourceLon?.toFixed(5)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Target Spot */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-xs font-semibold text-green-600 uppercase">Target (Keep)</span>
            </div>
            <h4 className="font-bold text-gray-900 mb-3">{data.targetspotname}</h4>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Sessions</span>
                <span className="font-semibold text-gray-900">{data.targetnsessions ?? 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Reviews</span>
                <span className="font-semibold text-gray-900">{data.targetnreviews ?? 0}</span>
              </div>
              {hasValidCoords && (
                <div className="pt-2 border-t border-green-200">
                  <div className="text-xs text-gray-400">
                    {data.targetLat?.toFixed(5)}, {data.targetLon?.toFixed(5)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Distance indicator */}
        {hasValidCoords && (
          <div className="flex items-center justify-center gap-2 mb-4 p-3 bg-gray-100 rounded-lg">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            </svg>
            <span className="text-sm text-gray-600">
              Distance between spots: <strong>{distance.toFixed(2)} km</strong>
            </span>
          </div>
        )}

        {/* Summary after merge */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="text-sm text-blue-800">
            <strong>After merge:</strong> "{data.targetspotname}" will have{" "}
            <strong>{(data.targetnsessions ?? 0) + (data.sourcensessions ?? 0)}</strong> sessions and{" "}
            <strong>{(data.targetnreviews ?? 0) + (data.sourcenreviews ?? 0)}</strong> reviews.
          </div>
        </div>

        {/* Protected Spot Error */}
        {isProtectedSpot(data.selectedspotid) && (
          <div className="bg-red-50 border border-red-300 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div>
                <div className="font-semibold text-red-800">Protected Spot</div>
                <div className="text-sm text-red-700">
                  The source spot "{data.sourcespotname}" (ID: {data.selectedspotid}) is a protected system spot and cannot be merged or deleted. This request cannot be approved.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Warning */}
        {!isProtectedSpot(data.selectedspotid) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="text-sm text-amber-800">
                This will permanently delete "{data.sourcespotname}" and transfer all data to "{data.targetspotname}".
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Check if merge request involves a protected spot
  const isMergeRequestProtected = (): boolean => {
    if (request.type === "merge") {
      const data = request.data as MergeSpotRequest;
      return isProtectedSpot(data.selectedspotid);
    }
    return false;
  };

  const renderAddReviewContent = () => {
    const data = request.data as SpotAddRequest;

    return (
      <>
        {/* Header */}
        <div className="text-center mb-5">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900">Add Spot Request</h3>
          <div className="flex justify-center">
            {renderUserLink(data.userid, data.username)}
          </div>
        </div>

        {/* Spot details */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="text-xs font-semibold text-green-600 uppercase mb-2">New Spot</div>
          <h4 className="font-bold text-xl text-gray-900 mb-3">"{data.spotname}"</h4>

          <div className="space-y-2">
            {data.countryName && (
              <div className="flex items-center gap-2 text-sm">
                {data.alpha2 && (
                  <img
                    src={`https://flagcdn.com/w20/${data.alpha2.toLowerCase()}.png`}
                    alt={data.alpha2}
                    className="w-5 h-auto"
                  />
                )}
                <span className="text-gray-700">{data.countryName}</span>
              </div>
            )}
            <div className="text-sm text-gray-500">
              Coordinates: {data.latitude.toFixed(6)}, {data.longitude.toFixed(6)}
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Approving will create this spot in the database. The requesting user will be notified.
        </p>
      </>
    );
  };

  const renderRenameReviewContent = () => {
    const data = request.data as SpotAddRequest;

    return (
      <>
        {/* Header */}
        <div className="text-center mb-5">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900">Rename Request</h3>
          <div className="flex justify-center">
            {renderUserLink(data.userid, data.username)}
          </div>
        </div>

        {/* Name change visualization */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="mb-4">
            <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Current Name</div>
            <div className="font-medium text-lg text-gray-600 line-through">"{data.oldname}"</div>
          </div>

          <div className="flex justify-center my-3">
            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>

          <div>
            <div className="text-xs font-semibold text-blue-600 uppercase mb-1">Proposed Name</div>
            <div className="font-bold text-xl text-blue-700">"{data.newname}"</div>
          </div>
        </div>

        {/* Location info */}
        {data.countryName && (
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            {data.alpha2 && (
              <img
                src={`https://flagcdn.com/w20/${data.alpha2.toLowerCase()}.png`}
                alt={data.alpha2}
                className="w-5 h-auto"
              />
            )}
            <span>{data.countryName}</span>
          </div>
        )}

        <p className="text-sm text-gray-600 mb-4">
          Approving will rename this spot. The requesting user will be notified.
        </p>
      </>
    );
  };

  const renderEditReviewContent = () => {
    const data = request.data as SpotAddRequest;
    const nameChanged = data.oldname != null && data.oldname !== data.newname;
    const locationChanged = data.oldlatitude != null && data.oldlongitude != null &&
      (Math.abs(data.latitude - data.oldlatitude) > 0.0001 || Math.abs(data.longitude - data.oldlongitude) > 0.0001);
    const distance = locationChanged
      ? calculateDistance(data.oldlatitude!, data.oldlongitude!, data.latitude, data.longitude)
      : 0;

    return (
      <>
        {/* Header */}
        <div className="text-center mb-5">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900">Spot Edit Request</h3>
          <div className="flex justify-center">
            {renderUserLink(data.userid, data.username)}
          </div>
        </div>

        {/* Changes summary */}
        <div className="space-y-4 mb-4">
          {/* Name change */}
          {nameChanged && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Name Change</div>
              <div className="mb-2">
                <span className="text-gray-500 text-sm">Current: </span>
                <span className="font-medium text-gray-600 line-through">"{data.oldname}"</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
              <div className="mt-2">
                <span className="text-gray-500 text-sm">Proposed: </span>
                <span className="font-bold text-amber-700">"{data.newname}"</span>
              </div>
            </div>
          )}

          {/* Location change */}
          {locationChanged && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="text-xs font-semibold text-amber-600 uppercase mb-2">Location Change</div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Current</span>
                  <span className="text-gray-700 font-mono text-xs">
                    {data.oldlatitude?.toFixed(6)}, {data.oldlongitude?.toFixed(6)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Proposed</span>
                  <span className="text-amber-700 font-mono text-xs font-semibold">
                    {data.latitude.toFixed(6)}, {data.longitude.toFixed(6)}
                  </span>
                </div>
                <div className="pt-2 border-t border-amber-200 flex items-center gap-2">
                  <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  <span className="text-sm text-amber-800">
                    Distance: <strong>{distance.toFixed(2)} km</strong>
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Spot name (if name didn't change) */}
          {!nameChanged && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Spot</div>
              <div className="font-bold text-lg text-gray-900">"{data.newname || data.oldname}"</div>
            </div>
          )}
        </div>

        {/* Location info */}
        {data.countryName && (
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            {data.alpha2 && (
              <img
                src={`https://flagcdn.com/w20/${data.alpha2.toLowerCase()}.png`}
                alt={data.alpha2}
                className="w-5 h-auto"
              />
            )}
            <span>{data.countryName}</span>
          </div>
        )}

        <p className="text-sm text-gray-600 mb-4">
          Approving will {nameChanged && locationChanged ? "rename and relocate" : nameChanged ? "rename" : "relocate"} this spot. The requesting user will be notified.
        </p>
      </>
    );
  };

  const renderDeclineReasonInput = () => {
    if (!showDeclineReason) return null;

    return (
      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
        <label className="block text-sm font-medium text-red-700 mb-2">
          Reason for declining (optional)
        </label>
        <textarea
          value={declineReason}
          onChange={(e) => setDeclineReason(e.target.value)}
          placeholder="Enter a reason to notify the user..."
          className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
          rows={2}
          autoFocus
        />
      </div>
    );
  };

  const renderContent = () => {
    // For approve/decline confirmations (from map popup)
    if (actionType === "approve") {
      switch (request.type) {
        case "merge":
          return renderMergeReviewContent();
        case "add":
          return renderAddReviewContent();
        case "rename":
          return renderRenameReviewContent();
        default:
          return null;
      }
    }

    if (actionType === "decline") {
      return (
        <>
          <div className="text-center mb-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900">Decline Request</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Are you sure you want to decline this request? The requesting user will be notified.
          </p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (optional)
            </label>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Enter a reason for declining..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={3}
            />
          </div>
        </>
      );
    }

    // Review mode - show detailed info
    switch (request.type) {
      case "merge":
        return renderMergeReviewContent();
      case "add":
        return renderAddReviewContent();
      case "rename":
        return renderRenameReviewContent();
      case "edit":
        return renderEditReviewContent();
      default:
        return null;
    }
  };

  const renderActions = () => {
    if (actionType === "approve") {
      return (
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onApprove}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            Confirm Approve
          </button>
        </div>
      );
    }

    if (actionType === "decline") {
      return (
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onDecline(declineReason || undefined)}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            Confirm Decline
          </button>
        </div>
      );
    }

    // Review mode - three buttons
    return (
      <>
        {renderDeclineReasonInput()}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDecline}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading && showDeclineReason && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            {showDeclineReason ? "Confirm Decline" : "Decline"}
          </button>
          <button
            onClick={onApprove}
            disabled={isLoading || showDeclineReason || isMergeRequestProtected()}
            className={`flex-1 px-4 py-2.5 ${isMergeRequestProtected() ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2`}
            title={isMergeRequestProtected() ? "Cannot approve: source spot is protected" : ""}
          >
            {isLoading && !showDeclineReason && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {isMergeRequestProtected() ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {isMergeRequestProtected() ? "Protected" : "Approve"}
          </button>
        </div>
      </>
    );
  };

  const dialog = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {renderContent()}
          {renderActions()}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, document.body);
};

export default ConfirmActionDialog;
